import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, CalendarClock, Clock, ExternalLink, LayoutGrid, RefreshCw, ShieldAlert, type LucideIcon } from 'lucide-react';
import { IPD_TASKS_CHANGED_EVENT, staffAlertPath, wardTasksPath, type IpdTaskView } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { describeError, useAuth } from '@/lib/auth';
import { useRealtimeInvalidate } from '@/lib/realtime';
import { cn, timeOnly } from '@/lib/utils';
import { EmptyState, PageHeader } from '@/components/ui/layout';
import { Alert, Badge, Button, Skeleton } from '@/components/ui/primitives';
import { RecordTaskDialog } from './RecordTaskDialog';
import { OrderBadges, TaskStatusChip } from './TaskChip';
import { taskTitle } from './task-labels';
import { TASK_READ_PERMISSIONS, fetchWardTasks, mayRecordTask, storeRecordedTask, useStaffDuty, useTaskClock, wardTasksKey } from './task-queries';
import { WARD_BUCKETS, WARD_NEXT_HORIZON_MINUTES, wardBoard, type WardBoardPatient, type WardBucket } from './ward-board';
import { WARD_PICKER_PREFIX, wardNameFrom, type WardOption } from './ward-picker';

const BUCKET_LOOK: Readonly<Record<WardBucket, { icon: LucideIcon; titleKey: string; emptyKey: string; tone: string; badge: 'destructive' | 'warning' | 'info' }>> = {
  overdue: { icon: AlarmClock, titleKey: 'io_ward_group_overdue', emptyKey: 'io_ward_empty_overdue', tone: 'text-destructive', badge: 'destructive' },
  due: { icon: Clock, titleKey: 'io_ward_group_due', emptyKey: 'io_ward_empty_due', tone: 'text-warning', badge: 'warning' },
  next: { icon: CalendarClock, titleKey: 'io_ward_group_next', emptyKey: 'io_ward_empty_next', tone: 'text-info', badge: 'info' },
};
const SKELETON_ROWS = ['s1', 's2'];

interface Recording {
  task: IpdTaskView;
  patientLabel: string;
}

export function WardTaskBoardPage() {
  const { wardId = '' } = useParams();
  const { can, hasModule } = useAuth();
  const queryClient = useQueryClient();
  const allowed = hasModule('ipd') && can(...TASK_READ_PERMISSIONS);
  const [recording, setRecording] = useState<Recording | null>(null);
  const duty = useStaffDuty(allowed);

  useRealtimeInvalidate(allowed && wardId ? [`ward:${wardId}`] : [], [IPD_TASKS_CHANGED_EVENT], [wardTasksKey(wardId)]);
  const board = useQuery({
    queryKey: wardTasksKey(wardId),
    queryFn: () => fetchWardTasks(wardId),
    enabled: allowed && Boolean(wardId),
  });
  const now = useTaskClock(board.data?.serverTime, board.dataUpdatedAt);
  const view = useMemo(() => wardBoard(board.data, now), [board.data, now]);

  const dutyWards = duty.data?.wards ?? [];
  const pickerLists = queryClient.getQueriesData<WardOption[]>({ queryKey: WARD_PICKER_PREFIX }).map(([, data]) => data);
  const ward = wardNameFrom(wardId, [dutyWards, ...pickerLists]);
  const otherDutyWards = dutyWards.filter((entry) => entry.id !== wardId);

  if (!allowed) {
    return (
      <>
        <PageHeader title={tr('ipd', 'io_ward_board_title')} />
        <EmptyState icon={ShieldAlert} title={tr('ipd', 'io_ward_not_allowed')} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={ward ? ward.name : tr('ipd', 'io_ward_board_title')}
        description={ward ? tr('ipd', 'io_ward_board_desc_named', { code: ward.code }) : tr('ipd', 'io_ward_board_desc')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {can('bed.read') && (
              <Link to="/ipd/wards?all=1">
                <Button size="sm" variant="outline">
                  <LayoutGrid className="h-4 w-4" aria-hidden /> {tr('ipd', 'io_ward_all_wards')}
                </Button>
              </Link>
            )}
            <Button size="sm" variant="outline" onClick={() => void board.refetch()} loading={board.isFetching} aria-label={tr('ipd', 'io_ward_refresh')}>
              <RefreshCw className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        }
      />

      {otherDutyWards.length > 0 && (
        <nav aria-label={tr('ipd', 'io_ward_switch')} className="mb-4 flex flex-wrap items-center gap-1.5">
          <span className="me-1 text-xs text-muted-foreground">{tr('ipd', 'io_ward_switch')}</span>
          {otherDutyWards.map((other) => (
            <Link key={other.id} to={wardTasksPath(other.id)} className="focus-ring inline-flex h-8 items-center rounded-full border bg-background px-3 text-sm font-medium hover:bg-muted/60">
              {other.name}
            </Link>
          ))}
        </nav>
      )}

      {board.isError && (
        <Alert variant="destructive" className="mb-4">
          {describeError(board.error)}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {WARD_BUCKETS.map((bucket) => (
          <BoardGroup
            key={bucket}
            bucket={bucket}
            count={view.counts[bucket]}
            patients={view.groups[bucket]}
            loading={board.isLoading}
            can={can}
            onRecord={(task, patientLabel) => setRecording({ task, patientLabel })}
          />
        ))}
      </div>

      {recording !== null && (
        <RecordTaskDialog
          task={recording.task}
          patientLabel={recording.patientLabel}
          onClose={() => setRecording(null)}
          onSaved={(saved) => {
            setRecording(null);
            storeRecordedTask(queryClient, saved, wardId);
          }}
        />
      )}
    </>
  );
}

function patientLabelOf(patient: WardBoardPatient['patient']): string {
  return patient.bedCode ? tr('ipd', 'io_ward_patient_label', { bed: patient.bedCode, patient: patient.patientName }) : patient.patientName;
}

function BoardGroup({
  bucket,
  count,
  patients,
  loading,
  can,
  onRecord,
}: {
  bucket: WardBucket;
  count: number;
  patients: WardBoardPatient[];
  loading: boolean;
  can: (...permissions: string[]) => boolean;
  onRecord: (task: IpdTaskView, patientLabel: string) => void;
}) {
  const look = BUCKET_LOOK[bucket];
  const Icon = look.icon;
  const title = tr('ipd', look.titleKey, { hours: WARD_NEXT_HORIZON_MINUTES / 60 });
  return (
    <section aria-label={title} className="flex min-w-0 flex-col rounded-2xl border bg-card shadow-sm">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <Icon className={cn('h-4 w-4 shrink-0', look.tone)} aria-hidden />
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</h2>
        <Badge variant={count > 0 ? look.badge : 'muted'} className="tabular">
          {count}
        </Badge>
      </header>
      {loading && (
        <div className="space-y-2 p-4">
          {SKELETON_ROWS.map((key) => (
            <Skeleton key={key} className="h-12 w-full" />
          ))}
        </div>
      )}
      {!loading && patients.length === 0 && <p className="px-4 py-6 text-center text-sm text-muted-foreground">{tr('ipd', look.emptyKey)}</p>}
      {patients.map((group) => (
        <PatientTasks key={group.patient.admissionId} group={group} can={can} onRecord={onRecord} />
      ))}
    </section>
  );
}

function PatientTasks({ group, can, onRecord }: { group: WardBoardPatient; can: (...permissions: string[]) => boolean; onRecord: (task: IpdTaskView, patientLabel: string) => void }) {
  const { patient } = group;
  const chartPath = staffAlertPath({ type: 'admission_tasks', id: patient.admissionId }) ?? `/ipd/admissions/${patient.admissionId}/emr`;
  const label = patientLabelOf(patient);
  return (
    <div className="border-b px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-semibold tabular">{patient.bedCode ?? '—'}</span>
        <Link to={chartPath} className="focus-ring inline-flex min-w-0 flex-1 items-center gap-1 rounded font-medium hover:underline">
          <span className="truncate">{patient.patientName || patient.admissionNumber}</span>
          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground rtl:-scale-x-100" aria-hidden />
        </Link>
        {patient.mrn && <span className="text-xs text-muted-foreground tabular">{tr('ipd', 'io_ward_mrn', { mrn: patient.mrn })}</span>}
      </div>
      <ul className="mt-2 space-y-2">
        {group.entries.map((entry) => (
          <li key={entry.task.id} className="flex items-start gap-2">
            <span className="w-16 shrink-0 pt-0.5 text-xs font-medium tabular">{timeOnly(entry.task.scheduledAt)}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium" title={entry.task.order.label}>
                {entry.task.order.label}
              </p>
              <p className="truncate text-xs text-muted-foreground">{taskTitle(entry.task)}</p>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <TaskStatusChip status={entry.status} />
                <OrderBadges priority={entry.task.order.priority} isHighAlert={entry.task.order.isHighAlert} state={entry.task.order.state} />
              </div>
            </div>
            {mayRecordTask(can, entry.task) && (
              <Button size="xs" variant={entry.lapsed ? 'outline' : 'default'} onClick={() => onRecord(entry.task, label)}>
                {tr('ipd', 'record')}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
